<script lang="ts">
  /**
   * Esforço administrativo de correções manuais.
   * Escala própria — corrigir ponto consome MUITAS horas-pessoa, mas em valor
   * unitário é pequeno comparado a hora extra. Aqui mostramos quantidade
   * (barras) na sua escala natural + KPIs de tempo total e custo.
   */
  import type { DailyAnalyticsPoint } from '$core/metrics/metrics.types';
  import {
    makeGeometry,
    formatDateShort,
    formatBrlCompact,
    formatMinutesCompact,
    niceMax,
    thinLabels,
  } from './chartUtils';

  export let daily: DailyAnalyticsPoint[];
  export let correctionMinutesPerEvent: number = 5;
  export let hourlyRateBrl: number | undefined = undefined;
  export let width: number = 720;
  export let height: number = 200;

  const geom = makeGeometry(width, height, { left: 48, bottom: 28 });
  const TICKS = [0, 0.25, 0.5, 0.75, 1];

  $: model = (() => {
    const maxValue = niceMax(Math.max(1, ...daily.map(d => d.correctionEvents)));
    const baseY = geom.padding.top + geom.innerHeight;
    const stepX = daily.length > 1 ? geom.innerWidth / (daily.length - 1) : geom.innerWidth;
    const bandWidth = Math.max(2, daily.length > 0 ? geom.innerWidth / daily.length * 0.78 : 0);

    const toX = (i: number) => daily.length <= 1
      ? geom.padding.left + geom.innerWidth / 2
      : geom.padding.left + i * stepX;
    const toY = (v: number) => baseY - Math.min(1, v / maxValue) * geom.innerHeight;

    const bars = daily.map((d, i) => ({
      x: toX(i) - bandWidth / 2,
      labelX: toX(i),
      y: toY(d.correctionEvents),
      h: Math.max(0, baseY - toY(d.correctionEvents)),
      visible: d.correctionEvents > 0,
    }));

    return {
      maxValue,
      bandWidth,
      bars,
      gridlines: TICKS.map(t => ({ t, y: toY(maxValue * t), label: Math.round(maxValue * t).toString() })),
    };
  })();

  $: totalCorrections = daily.reduce((acc, d) => acc + d.correctionEvents, 0);
  $: totalCorrectionMinutes = totalCorrections * correctionMinutesPerEvent;
  $: totalCostBrl = daily.reduce((acc, d) => acc + d.costCorrectionBrl, 0);
  $: peak = daily.reduce<DailyAnalyticsPoint | null>((best, d) => {
    if (!best || d.correctionEvents > (best.correctionEvents ?? 0)) return d;
    return best;
  }, null);
  $: showLabel = thinLabels(daily, 8);
  $: hasData = totalCorrections > 0;
</script>

<div class="chart">
  <header class="chart__head">
    <div>
      <h3 class="chart__title">Esforço de correções manuais</h3>
      <p class="chart__subtitle">Jornadas que precisaram de ajuste (par ímpar ou marcação corrigida) por dia</p>
    </div>
    <div class="chart__kpis">
      <div class="kpi">
        <span class="kpi__label">Correções no período</span>
        <span class="kpi__value">{totalCorrections}</span>
      </div>
      <div class="kpi">
        <span class="kpi__label">Tempo total</span>
        <span class="kpi__value">{formatMinutesCompact(totalCorrectionMinutes)}</span>
      </div>
      {#if hourlyRateBrl !== undefined && hourlyRateBrl > 0}
        <div class="kpi">
          <span class="kpi__label">Custo administrativo</span>
          <span class="kpi__value kpi__value--alert">{formatBrlCompact(totalCostBrl)}</span>
        </div>
      {/if}
      {#if peak && peak.correctionEvents > 0}
        <div class="kpi">
          <span class="kpi__label">Pico ({formatDateShort(peak.date)})</span>
          <span class="kpi__value">{peak.correctionEvents}</span>
        </div>
      {/if}
    </div>
  </header>

  {#if !hasData}
    <p class="empty">Nenhuma jornada precisou de correção no período — pares de marcação consistentes.</p>
  {:else}
    <svg viewBox="0 0 {width} {height}" preserveAspectRatio="none" role="img" aria-label="Correções manuais por dia">
      {#each model.gridlines as g}
        <line
          x1={geom.padding.left}
          x2={geom.padding.left + geom.innerWidth}
          y1={g.y}
          y2={g.y}
          stroke="#E7E7E7"
          stroke-dasharray={g.t === 0 ? '0' : '2 4'}
          stroke-width="1"
        />
        <text x={geom.padding.left - 6} y={g.y + 4} font-size="10" text-anchor="end" fill="#7D7D7D">{g.label}</text>
      {/each}

      {#each model.bars as bar}
        {#if bar.visible}
          <rect x={bar.x} y={bar.y} width={model.bandWidth} height={bar.h} fill="#2062E1" opacity="0.85" rx="1.5" />
        {/if}
      {/each}

      {#each daily as d, i}
        {#if showLabel[i]}
          <text
            x={model.bars[i]?.labelX ?? 0}
            y={geom.padding.top + geom.innerHeight + 16}
            font-size="10"
            text-anchor="middle"
            fill="#565656"
          >{formatDateShort(d.date)}</text>
        {/if}
      {/each}
    </svg>

    <p class="footnote">
      Cada jornada com par ímpar ou ajuste manual custa ~{correctionMinutesPerEvent}min de DP (premissa configurável).
      Em volumes altos vira gargalo no fechamento de folha — não no payroll, mas no time administrativo.
    </p>
  {/if}
</div>

<style>
  .chart { display: flex; flex-direction: column; gap: var(--ptt-space-3); }
  .chart__head { display: flex; justify-content: space-between; gap: var(--ptt-space-4); flex-wrap: wrap; align-items: flex-end; }
  .chart__title { margin: 0; font-size: 1rem; font-weight: 700; color: var(--ptt-gray-300); }
  .chart__subtitle { margin: 2px 0 0; font-size: 0.8125rem; color: var(--ptt-gray-100); }
  .chart__kpis { display: flex; gap: var(--ptt-space-4); flex-wrap: wrap; }
  .kpi { display: flex; flex-direction: column; line-height: 1.1; }
  .kpi__label { font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--ptt-gray-100); }
  .kpi__value { font-size: 1.125rem; font-weight: 700; color: var(--ptt-gray-300); margin-top: 2px; }
  .kpi__value--alert { color: var(--ptt-orange-100); }
  .empty { font-size: 0.875rem; color: var(--ptt-gray-100); margin: 0; padding: var(--ptt-space-3); background: var(--ptt-gray-10); border-radius: var(--ptt-radius-sm); }
  .footnote { margin: 0; font-size: 0.75rem; color: var(--ptt-gray-100); line-height: 1.4; }
  svg { width: 100%; height: auto; display: block; }
</style>
